import { useQuery } from "@tanstack/react-query";

import {
  authedFetch,
  hasStoredSession,
  useStoredAuth,
} from "nazare-ui/utils";

import { resolveBffDomain } from "./bff-domain";
import { withAbortTimeout } from "./workflow-history-timeout";
import {
  isWorkflowHistoryDateRange,
  WORKFLOW_HISTORY_STATUS_VALUES,
} from "./workflow-history-url-state";
import type {
  WorkflowStatusHistoryResponse,
} from "./workflows-api";

const WORKFLOWS_BASE = `${resolveBffDomain()}/workflows`;

export type WorkflowHistoryPeriod = "1h" | "24h" | "7d" | "30d" | "custom";

export type WorkflowHistoryQuery = {
  period: WorkflowHistoryPeriod;
  workflow: string[];
  status: string[];
  from?: string;
  to?: string;
};

const PRESET_PERIODS = new Set<Exclude<WorkflowHistoryPeriod, "custom">>([
  "1h",
  "24h",
  "7d",
  "30d",
]);
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isValidDate(value: string | undefined): value is string {
  if (!value || !ISO_DATE.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

export function useFilteredWorkflowStatusHistory(filters: WorkflowHistoryQuery) {
  const auth = useStoredAuth();

  return useQuery({
    queryKey: ["workflows", "status-history", filters],
    enabled: hasStoredSession(auth),
    queryFn: ({ signal }) => {
      const customFrom = isValidDate(filters.from) ? filters.from : undefined;
      const customTo = isValidDate(filters.to) ? filters.to : undefined;
      const hasValidCustomRange =
        filters.period === "custom" &&
        isWorkflowHistoryDateRange(customFrom, customTo);
      const presetPeriod = PRESET_PERIODS.has(
        filters.period as Exclude<WorkflowHistoryPeriod, "custom">,
      )
        ? filters.period as Exclude<WorkflowHistoryPeriod, "custom">
        : "24h";
      const period = hasValidCustomRange
        ? "custom"
        : presetPeriod;
      const params = new URLSearchParams({ source: "aws", period });
      if (hasValidCustomRange && customFrom && customTo) {
        params.set("from", customFrom);
        params.set("to", customTo);
      }
      filters.workflow.filter(Boolean).forEach((value) => params.append("workflowName", value));
      filters.status
        .filter((value) => WORKFLOW_HISTORY_STATUS_VALUES.includes(
          value as (typeof WORKFLOW_HISTORY_STATUS_VALUES)[number],
        ))
        .forEach((value) => params.append("status", value));

      return withAbortTimeout(
        async (requestSignal) => {
          const response = await authedFetch(
            WORKFLOWS_BASE,
            auth,
            `/history?${params}`,
            { signal: requestSignal },
          );
          if (!response.ok) {
            throw new Error(`Failed to fetch workflow status history: ${response.status}`);
          }
          return response.json() as Promise<WorkflowStatusHistoryResponse>;
        },
        35_000,
        signal,
      );
    },
    retry: false,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
